import React, { createContext, useContext, useState, useEffect } from 'react';

const DispatchContext = createContext();

const STORAGE_CART = 'dispatchCart';
const STORAGE_ORDER = 'dispatchOrder';

// 🔑 Unique key generator
export const getVariantKey = (product) =>`${product.clientname}_${product.clientId}_${product.SOAutoID}_${product.SODtsID}`;

export const DispatchProvider = ({ children }) => {
    // Load cart from localStorage
    const [cart, setCart] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_CART)) || {};
        } catch {
            return {};
        }
    });

    // Load item order from localStorage
    const [itemOrder, setItemOrder] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_ORDER)) || [];
        } catch {
            return [];
        }
    });

    // Persist both cart and order to localStorage on every change
    useEffect(() => {
        localStorage.setItem(STORAGE_CART, JSON.stringify(cart));
        localStorage.setItem(STORAGE_ORDER, JSON.stringify(itemOrder));
    }, [cart, itemOrder]);

    // ➕ Add or update a product in dispatch cart
    const addItem = (product) => {
        const variantKey = getVariantKey(product);
        const itemWithKey = { ...product, variantKey };

        setCart(prev => ({
            ...prev,
            [variantKey]: itemWithKey  // ✅ store full object with all props
        }));

        setItemOrder(prev => {
            return prev.includes(variantKey) ? prev : [...prev, variantKey];
        });
    };

    // ✏️ Update dispatch quantity
    const updateDispatchQty = (variantKey, qty) => {
        if (!variantKey || typeof qty !== 'number') return;

        setCart(prev => {
            if (!prev[variantKey]) return prev;

            return {
                ...prev,
                [variantKey]: {
                    ...prev[variantKey],
                    dispatchQty: qty
                }
            };
        });
    };

    // ❌ Remove a product from the cart
    const removeItem = (variantKey) => {
        setCart(prev => {
            const { [variantKey]: _, ...rest } = prev;
            return rest;
        });

        setItemOrder(prev => prev.filter(key => key !== variantKey));
    };

    // ✅ New: remove multiple items by their variant keys
    const removeItemsByVariantKeys = (variantKeys = []) => {
        setCart(prev => {
            const newCart = { ...prev };
            variantKeys.forEach(key => delete newCart[key]);
            return newCart;
        });

        setItemOrder(prev => prev.filter(key => !variantKeys.includes(key)));
    };

    // 🧹 Clear entire cart
    const clearCart = () => {
        setCart({});
        setItemOrder([]);
    };

    // 📦 Get all items by clientId
    const getCartByClient = (clientId) => {
        return Object.values(cart).filter(item => String(item.clientId) === String(clientId));
    };

    return (
        <DispatchContext.Provider value={{
            cart,
            itemOrder,
            addItem,
            updateDispatchQty,
            removeItem,
            removeItemsByVariantKeys,
            clearCart,
            getCartByClient,
        }}>
            {children}
        </DispatchContext.Provider>
    );
};

export const useDispatchCart = () => useContext(DispatchContext);